import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@vehix/portal/components/ui/dialog';
import { Button } from '@vehix/portal/components/ui/button';
import { Label } from '@vehix/portal/components/ui/label';
import { Input } from '@vehix/portal/components/ui/input';
import { Checkbox } from '@vehix/portal/components/ui/checkbox';
import { Separator } from '@vehix/portal/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@vehix/portal/components/ui/collapsible';
import { Badge } from '@vehix/portal/components/ui/badge';
import { ChevronDown, X, Plus, Settings, Play } from 'lucide-react';
import { useAllTenants } from '@vehix/portal/hooks/useTenants';
import { useAvailableZones, useRunPipeline } from '../../hooks/usePipelineOrchestrator';
import { useAuth } from '@vehix/shared/admin/portal-bridge';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import { PipelineZoneSelector, type ZoneSelection } from './PipelineZoneSelector';
import type { PipelineRunRequest } from '../../services/pipelineOrchestrator';

// ============================================================================
// TYPES
// ============================================================================

interface PipelineCreationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTenantId?: string | undefined;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const PipelineCreationDialog: React.FC<PipelineCreationDialogProps> = ({
  open,
  onOpenChange,
  defaultTenantId
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const runPipeline = useRunPipeline();

  // Form state
  const [tenantId, setTenantId] = useState(defaultTenantId || '');
  const [selectedZones, setSelectedZones] = useState<ZoneSelection[]>([]);
  // Full is the default: an on-demand run is almost always wanted as a complete
  // refresh, and 'auto' left the mode to the orchestrator's own heuristic.
  const [runMode, setRunMode] = useState<'auto' | 'full' | 'delta'>('full');
  const [vinProcessorType, setVinProcessorType] = useState<'api' | 'selenium'>('api');
  const [skipVinProcessing, setSkipVinProcessing] = useState(false);
  const [enableDedup, setEnableDedup] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState<string[]>(['engineering@kebi.ai']);
  const [emailInput, setEmailInput] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Data fetching
  const { data: tenants, isLoading: tenantsLoading } = useAllTenants();
  const {
    data: availableZones,
    isLoading: zonesLoading,
    error: zonesError
  } = useAvailableZones(tenantId);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTenantId(defaultTenantId || '');
      setSelectedZones([]);
      setRunMode('full');
      setVinProcessorType('api');
      setSkipVinProcessing(false);
      setEnableDedup(false);
      setNotificationEmails(['engineering@kebi.ai']);
      setEmailInput('');
      setAdvancedOpen(false);
      setErrors({});
    }
  }, [open, defaultTenantId]);

  // Clear zones when tenant changes
  useEffect(() => {
    setSelectedZones([]);
  }, [tenantId]);

  // ========================================================================
  // EMAIL CHIPS
  // ========================================================================

  const handleAddEmail = useCallback(() => {
    const email = emailInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !notificationEmails.includes(email)) {
      setNotificationEmails(prev => [...prev, email]);
      setEmailInput('');
    }
  }, [emailInput, notificationEmails]);

  const handleRemoveEmail = useCallback((email: string) => {
    setNotificationEmails(prev => prev.filter(e => e !== email));
  }, []);

  const handleEmailKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddEmail();
      }
    },
    [handleAddEmail]
  );

  // ========================================================================
  // VALIDATION & SUBMIT
  // ========================================================================

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!tenantId) newErrors['tenantId'] = 'Tenant is required';
    if (selectedZones.length === 0) newErrors['zones'] = 'Select at least one zone';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const request: PipelineRunRequest = {
      tenant_id: tenantId,
      zones: selectedZones.flatMap(z =>
        z.collectors.map(collector => ({
          zone_slug: z.zone_slug,
          collector
        }))
      ),
      ...(runMode !== 'auto' && { run_mode: runMode }),
      ...(vinProcessorType !== 'api' && { vin_processor_type: vinProcessorType }),
      ...(skipVinProcessing && { skip_vin_processing: skipVinProcessing }),
      ...(enableDedup && { enable_dedup: true }),
      ...(notificationEmails.length > 0 && { notification_emails: notificationEmails }),
      ...(user?.id && { requested_by: user.id })
    };

    // Instant feedback: close dialog immediately
    onOpenChange(false);
    toast({ title: 'Pipeline started', description: 'Your pipeline is being initialized...' });

    runPipeline.mutate(request, {
      onError: () => {
        toast({ title: 'Failed to start pipeline', variant: 'destructive' });
      }
    });
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Run Pipeline</DialogTitle>
          <DialogDescription>
            Configure and start a new data pipeline for collecting and processing inventory.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-5 py-2'>
          {/* Tenant Picker */}
          <div className='space-y-2'>
            <Label>Tenant</Label>
            <Select value={tenantId} onValueChange={setTenantId}>
              <SelectTrigger className={errors['tenantId'] ? 'border-red-500' : ''}>
                <SelectValue placeholder={tenantsLoading ? 'Loading...' : 'Select tenant'} />
              </SelectTrigger>
              <SelectContent>
                {(tenants || []).map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.tenant_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors['tenantId'] && <p className='text-xs text-red-500'>{errors['tenantId']}</p>}
          </div>

          <Separator />

          {/* Zone Selector */}
          <div className='space-y-2'>
            <Label>
              Zones{' '}
              {selectedZones.length > 0 && (
                <Badge variant='secondary' className='ml-1'>
                  {selectedZones.length} selected
                </Badge>
              )}
            </Label>
            {errors['zones'] && <p className='text-xs text-red-500'>{errors['zones']}</p>}

            <PipelineZoneSelector
              zones={availableZones}
              value={selectedZones}
              onChange={setSelectedZones}
              isLoading={zonesLoading}
              loadError={zonesError}
              placeholder={tenantId ? undefined : 'Select a tenant first'}
              invalid={!!errors['zones']}
            />
          </div>

          <Separator />

          {/* Run Mode */}
          <div className='space-y-2'>
            <Label>Run Mode</Label>
            <div className='grid grid-cols-3 gap-2'>
              {[
                {
                  value: 'auto' as const,
                  label: 'Auto',
                  desc: 'Full run if first time, delta otherwise'
                },
                { value: 'full' as const, label: 'Full Run', desc: 'Re-crawl all inventory' },
                {
                  value: 'delta' as const,
                  label: 'Delta Only',
                  desc: 'Process existing inventory'
                }
              ].map(mode => (
                <button
                  key={mode.value}
                  type='button'
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    runMode === mode.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                  onClick={() => setRunMode(mode.value)}
                >
                  <p className='text-sm font-medium'>{mode.label}</p>
                  <p className='text-xs text-muted-foreground mt-0.5'>{mode.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* VIN Processor Type */}
          <div className='space-y-2'>
            <Label>VIN Processor</Label>
            <div className='grid grid-cols-2 gap-2'>
              {[
                { value: 'api' as const, label: 'API-based', desc: 'Fast, uses HTTP APIs' },
                {
                  value: 'selenium' as const,
                  label: 'Selenium-based',
                  desc: 'Browser automation, slower'
                }
              ].map(processor => (
                <button
                  key={processor.value}
                  type='button'
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    vinProcessorType === processor.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                  onClick={() => setVinProcessorType(processor.value)}
                >
                  <p className='text-sm font-medium'>{processor.label}</p>
                  <p className='text-xs text-muted-foreground mt-0.5'>{processor.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Advanced Options */}
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <Button variant='ghost' size='sm' className='gap-1 text-muted-foreground'>
                <Settings className='h-3.5 w-3.5' />
                Advanced Options
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${advancedOpen ? 'rotate-180' : ''}`}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className='space-y-4 pt-3'>
              {/* Skip VIN Processing */}
              <div className='flex items-center gap-2'>
                <Checkbox
                  id='skip-vin'
                  checked={skipVinProcessing}
                  onCheckedChange={checked => {
                    const on = checked === true;
                    setSkipVinProcessing(on);
                    if (on) setEnableDedup(false);
                  }}
                />
                <Label htmlFor='skip-vin' className='text-sm font-normal cursor-pointer'>
                  Skip VIN processing (collection only)
                </Label>
              </div>

              {/* Enable Dedup */}
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <Checkbox
                    id='enable-dedup'
                    checked={enableDedup}
                    disabled={skipVinProcessing}
                    onCheckedChange={checked => {
                      const on = checked === true;
                      setEnableDedup(on);
                      if (on) setSkipVinProcessing(false);
                    }}
                  />
                  <Label
                    htmlFor='enable-dedup'
                    className={`text-sm font-normal cursor-pointer ${skipVinProcessing ? 'text-muted-foreground' : ''}`}
                  >
                    Skip VINs already processed today
                  </Label>
                </div>
                <p className='text-xs text-muted-foreground ml-6'>
                  When ON, the run skips VINs that were already successfully processed earlier today
                  (one VIN, one recall lookup per day). When OFF (default), every collected VIN in
                  scope is sent to the recall processor regardless of prior runs.
                </p>
              </div>

              {/* Notification Emails */}
              <div className='space-y-2'>
                <Label>Notification Emails</Label>
                <div className='flex gap-2'>
                  <Input
                    placeholder='email@example.com'
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    onKeyDown={handleEmailKeyDown}
                    className='flex-1'
                  />
                  <Button type='button' variant='outline' size='sm' onClick={handleAddEmail}>
                    <Plus className='h-3.5 w-3.5' />
                  </Button>
                </div>
                {notificationEmails.length > 0 && (
                  <div className='flex flex-wrap gap-1.5'>
                    {notificationEmails.map(email => (
                      <Badge key={email} variant='secondary' className='gap-1 pr-1'>
                        {email}
                        <button
                          type='button'
                          onClick={() => handleRemoveEmail(email)}
                          className='hover:bg-muted rounded-full p-0.5'
                        >
                          <X className='h-3 w-3' />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            <Play className='h-4 w-4 mr-1' />
            Run Pipeline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
